/**
 * Metadata Keywords Page
 * 
 * Displays aggregated keywords from all metadata with count, pagination, and category management
 */

import { useState, useEffect } from 'react';
import { Search, Tag, TrendingUp, Hash, Check, X, Filter, AlertCircle } from 'lucide-react';
import { adminApi } from '../stores/adminStore';

interface Category {
  id: string;
  name: string;
  slug: string;
  color: string;
  icon: string;
}

interface KeywordItem {
  keyword: string;
  count: number;
  category?: Category | null;
}

interface KeywordsResponse {
  keywords: KeywordItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    uniqueKeywords: number;
    totalKeywordUsage: number;
  };
}

export default function MetadataKeywordsPage() {
  const [keywords, setKeywords] = useState<KeywordItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [assigningCategory, setAssigningCategory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [pageInput, setPageInput] = useState('1');
  
  // Filter states
  const [minCount, setMinCount] = useState('');
  const [maxCount, setMaxCount] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uncategorizedOnly, setUncategorizedOnly] = useState(false);
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0
  });
  const [stats, setStats] = useState({
    uniqueKeywords: 0,
    totalKeywordUsage: 0
  });

  // Load categories
  const loadCategories = async () => {
    try {
      const response = await adminApi.get('/admin/metadata/categories');
      const data = response.data.data || response.data;
      setCategories(data.categories || []);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
    }
  };

  // Load keywords
  const loadKeywords = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (search.trim()) {
        params.append('search', search.trim());
      }

      // Add new filters
      if (minCount.trim()) {
        params.append('minCount', minCount.trim());
      }
      if (maxCount.trim()) {
        params.append('maxCount', maxCount.trim());
      }
      if (categoryFilter) {
        params.append('categoryId', categoryFilter);
      }
      if (uncategorizedOnly) {
        params.append('uncategorizedOnly', 'true');
      }

      const response = await adminApi.get(`/admin/metadata/keywords?${params}`);
      const data: KeywordsResponse = response.data.data || response.data;

      setKeywords(data.keywords || []);
      setPagination(data.pagination);
      setStats(data.stats);
    } catch (err: any) {
      console.error('Failed to load keywords:', err);
      setError('Failed to load keywords. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Load keywords when filters change
  useEffect(() => {
    loadKeywords();
  }, [page, search, minCount, maxCount, categoryFilter, uncategorizedOnly]);

  // Sync pageInput with page
  useEffect(() => {
    setPageInput(page.toString());
  }, [page]);

  // Handle search submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  // Handle page jump
  const handlePageJump = (e: React.FormEvent) => {
    e.preventDefault();
    const targetPage = parseInt(pageInput);
    if (!isNaN(targetPage) && targetPage >= 1 && targetPage <= pagination.totalPages) {
      setPage(targetPage);
    } else {
      setPageInput(page.toString());
    }
  };

  // Toggle keyword selection
  const toggleKeywordSelection = (keyword: string) => {
    const newSelected = new Set(selectedKeywords);
    if (newSelected.has(keyword)) {
      newSelected.delete(keyword);
    } else {
      newSelected.add(keyword);
    }
    setSelectedKeywords(newSelected);
  };

  // Select all keywords on current page
  const selectAllOnPage = () => {
    const newSelected = new Set(selectedKeywords);
    keywords.forEach(item => newSelected.add(item.keyword));
    setSelectedKeywords(newSelected);
  };

  // Deselect all keywords
  const deselectAll = () => {
    setSelectedKeywords(new Set());
  };

  // Clear all filters
  const clearFilters = () => {
    setMinCount('');
    setMaxCount('');
    setCategoryFilter('');
    setUncategorizedOnly(false);
    setPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = minCount || maxCount || categoryFilter || uncategorizedOnly;

  // Assign category to selected keywords
  const assignCategory = async () => {
    if (selectedKeywords.size === 0) {
      setError('Please select at least one keyword');
      return;
    }

    if (!selectedCategoryId) {
      setError('Please select a category');
      return;
    }

    setAssigningCategory(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await adminApi.post('/admin/metadata/keywords/assign-category', {
        keywords: Array.from(selectedKeywords),
        categoryId: selectedCategoryId
      });

      const data = response.data.data || response.data;
      setSuccessMessage(data.message || `Successfully assigned ${selectedKeywords.size} keywords`);
      
      // Clear selection and reload keywords
      setSelectedKeywords(new Set());
      setSelectedCategoryId('');
      await loadKeywords();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to assign category:', err);
      setError(err.response?.data?.message || 'Failed to assign category. Please try again.');
    } finally {
      setAssigningCategory(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Metadata Keywords</h1>
        <p className="text-gray-600">
          View and categorize keywords from metadata
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Tag className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Unique Keywords</p>
              <p className="text-2xl font-bold text-gray-900">{stats.uniqueKeywords.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Usage</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalKeywordUsage.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Hash className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Selected</p>
              <p className="text-2xl font-bold text-gray-900">{selectedKeywords.size}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Assignment Bar */}
      {selectedKeywords.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <p className="text-sm font-medium text-blue-900">
                {selectedKeywords.size} keyword{selectedKeywords.size !== 1 ? 's' : ''} selected
              </p>
              <button
                onClick={deselectAll}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="">Select category...</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <button
                onClick={assignCategory}
                disabled={assigningCategory || !selectedCategoryId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
              >
                {assigningCategory ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Assigning...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Assign Category
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <p className="text-green-700 flex items-center gap-2">
            <Check className="w-5 h-5" />
            {successMessage}
          </p>
          <button onClick={() => setSuccessMessage(null)} className="text-green-600 hover:text-green-800">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search keywords..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSearchInput('');
                setPage(1);
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              Clear All Filters
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Quantity Range */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Min Quantity
            </label>
            <input
              type="number"
              min="1"
              placeholder="e.g. 10"
              value={minCount}
              onChange={(e) => {
                setMinCount(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Max Quantity
            </label>
            <input
              type="number"
              min="1"
              placeholder="e.g. 100"
              value={maxCount}
              onChange={(e) => {
                setMaxCount(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Filter by Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setUncategorizedOnly(false); // Clear uncategorized when selecting category
                setPage(1);
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Uncategorized Only */}
          <div className="flex items-end">
            <label className="flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors w-full">
              <input
                type="checkbox"
                checked={uncategorizedOnly}
                onChange={(e) => {
                  setUncategorizedOnly(e.target.checked);
                  if (e.target.checked) {
                    setCategoryFilter(''); // Clear category filter when checking uncategorized
                  }
                  setPage(1);
                }}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex items-center gap-1">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">Uncategorized Only</span>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Keywords Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={keywords.length > 0 && keywords.every(k => selectedKeywords.has(k.keyword))}
                    onChange={(e) => e.target.checked ? selectAllOnPage() : deselectAll()}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Keyword
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Count
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Percentage
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-500">Loading keywords...</span>
                    </div>
                  </td>
                </tr>
              ) : keywords.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    {search ? 'No keywords found matching your search.' : 'No keywords found in metadata.'}
                  </td>
                </tr>
              ) : (
                keywords.map((item, index) => {
                  const globalIndex = (page - 1) * limit + index + 1;
                  const percentage = stats.totalKeywordUsage > 0 
                    ? ((item.count / stats.totalKeywordUsage) * 100).toFixed(2)
                    : '0.00';
                  const isSelected = selectedKeywords.has(item.keyword);

                  return (
                    <tr 
                      key={`${item.keyword}-${index}`} 
                      className={`hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleKeywordSelection(item.keyword)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {globalIndex}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{item.keyword}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.category ? (
                          <span 
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{ 
                              backgroundColor: `${item.category.color}15`,
                              color: item.category.color
                            }}
                          >
                            {item.category.name}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Uncategorized</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                        {item.count.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                        {percentage}%
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                disabled={page === pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((page - 1) * limit) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(page * limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> keywords
                </p>
              </div>
              <div className="flex items-center gap-4">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                    disabled={page === pagination.totalPages}
                    className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
                
                {/* Jump to page input */}
                <form onSubmit={handlePageJump} className="flex items-center gap-2">
                  <label htmlFor="page-jump" className="text-sm text-gray-700 whitespace-nowrap">
                    Go to:
                  </label>
                  <input
                    id="page-jump"
                    type="number"
                    min="1"
                    max={pagination.totalPages}
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onBlur={() => {
                      const val = parseInt(pageInput);
                      if (isNaN(val) || val < 1 || val > pagination.totalPages) {
                        setPageInput(page.toString());
                      }
                    }}
                    className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Page"
                  />
                  <button
                    type="submit"
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Go
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}